import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import ic_error from "../../assets/images/icons/cancel.svg";
import ic_success from "../../assets/images/icons/success.svg";
import { RootState } from "../../redux/store";
import i18n from "../../i18n";

interface ToastProps {
  message: string;
  description: string;
  type: "success" | "error";
  duration: number;
  onClose: () => void;
  isCentered?: boolean; // Optional prop to control centering
}

const Toast: React.FC<ToastProps> = ({
  message,
  description,
  type,
  duration,
  onClose,
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const isEnglish = i18n.language?.slice(0, 2) === "en";
  const { isAuthenticated, isRegistrationComplete, isPassedOptional } =
    useSelector((state: RootState) => state.user);

  // Determine gradient direction and color based on language and type
  const color = type === "success" ? "from-green-100" : "from-red-100";
  const gradientClass = ` ${isEnglish ? "bg-gradient-to-r" : "bg-gradient-to-l"} ${color} via-white to-white`;

  const borderColorClass =
    type === "success" ? "border-successGreen" : "border-error";
  const iconClass = type === "success" ? "text-successGreen" : "text-error";
  const textAlignClass = isEnglish ? "text-left" : "text-right arabic";
  const isCenterCondition = isAuthenticated && isRegistrationComplete;

  let positionClasses;
  if (isCenterCondition) {
    positionClasses = "flex-row left-1/2 -translate-x-1/2 -translate-y-28";
  }
  if (!isCenterCondition && isEnglish) {
    positionClasses =
      "flex-row left-1/2 -translate-x-1/2 lg:left-1/4 lg:-translate-x-1/2 ";
  }
  if (!isCenterCondition && !isEnglish) {
    positionClasses =
      "!flex-row-reverse right-1/2 translate-x-1/2 lg:right-1/4 lg:translate-x-1/2";
  }
  const spacingClass = isEnglish ? "space-x-4" : "space-x-4 space-x-reverse";

  return (
    <div
      dir={isEnglish ? "ltr" : "rtl"}
      className={`fixed bottom-5 top-48 z-50 ms-6 flex h-fit w-[90vw] transform items-center rounded-lg border-b-8 px-6 py-4 shadow-toast max-sm:w-80 lg:bottom-auto lg:w-[36rem] ${positionClasses} ${spacingClass} ${gradientClass} ${borderColorClass}`}
    >
      <div className="flex-shrink-0">
        {type === "success" ? (
          <img src={ic_success} alt="" className="shadow-toastIcon" />
        ) : (
          <img src={ic_error} alt="" className="shadow-toastIcon" />
        )}
      </div>
      <div>
        <p className={`${textAlignClass} text-primary-text font-semibold`}>
          {message}
        </p>
        <p className={`${textAlignClass} text-primary-text text-sm`}>
          {description}
        </p>
      </div>
    </div>
  );
};

export default Toast;
